import styled from '@emotion/styled';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { hubSpotSubmit } from '../../../utils/api/contentful';
import { prepareFormBody } from '../../../utils/helpers/formHandler';
import { breakpoints, colors, spacing } from '../../../utils/styleguide';
import PrimaryButton from '../buttons/PrimaryButton';
import DesktopVerticalCenter from '../DesktopVerticalCenter';
import TextInput, { validations } from '../forms/TextInput';
import Typography from '../text/Typography';
import { Col, Container, Row } from './12ColGridLayout';
import ButtonPromptWrapper from '../ButtonPromptWrapper';
import { IForm, IFormItem } from '../../../types/@types/generated/contentful';

const Section = styled(Container)`
  padding: ${spacing[6]}px 0;
  display: flex;
  min-height: ${720 - spacing[6]}px;
  background-color: ${colors.brightGrey};
  ${breakpoints.desktop} {
    padding: ${spacing[11]}px ${spacing[11]}px;
    min-height: ${720 - spacing[11]}px;
  }
`;

const SectionBlock = styled(Col)`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const RowBlock = styled(Row)`
  width: 100%;
`;

const FormBlock = styled(SectionBlock)`
  min-height: 390px;
  ${breakpoints.tablet} {
    min-height: 420px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const MessageWrapper = styled(Typography)`
  color: ${colors.darkGrey};
  text-align: center;
`;

const Input = styled(TextInput)`
  margin: ${spacing[2]}px 0;
  width: 100%;
  ${breakpoints.desktop} {
    margin-bottom: 0px;
    margin-right: ${spacing[3]}px;
    flex: 1;
  }
`;

const ContentTitle = styled.div`
  margin: ${spacing[4]}px 0;
  ${breakpoints.desktop} {
    margin: 0 0 ${spacing[5]}px;
  }
`;

const ButtonWrapper = styled(PrimaryButton)`
  margin-top: ${spacing[6]}px;
  width: 140px;
  height: 48px;
  line-height: 16px;
`;

type FormLayoutProps = {
  title?: ReactNode;
  description?: ReactNode;
  form: IForm;
  settings?: {
    identifier?: string;
  };
};

const getInitialForm = (formItems: IFormItem[]): Record<string, string> => {
  return formItems.reduce(
    (acc: Record<string, string>, { fields: { fieldName } }: IFormItem): Record<string, string> => {
      acc[fieldName] = '';
      return acc;
    },
    {},
  );
};

const checkFormEmpty = (formItems: IFormItem[], updatedForm: Record<string, string>): boolean => {
  return formItems.reduce((acc: boolean, { fields: { fieldName } }: IFormItem): boolean => {
    return acc && !updatedForm[fieldName];
  }, true);
};

const FormLayout = ({
  title,
  description,
  form: {
    fields: {
      submitText = 'Submit',
      url,
      successMessage = 'Thank you for keeping in touch!',
      errorMessage = 'Oops! Something went wrong, please try again later.',
      type,
      prompt,
      formItems,
    },
  },
  settings,
}: FormLayoutProps) => {
  const [formData, setFormData] = useState(getInitialForm(formItems));
  const [formEmpty, setFormEmpty] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const contentBlock = useMemo(
    () => (
      <DesktopVerticalCenter>
        <ContentTitle>{title}</ContentTitle>
        {description}
      </DesktopVerticalCenter>
    ),
    [title, description],
  );

  const contentForm = useMemo(() => {
    return formItems.map(
      ({ fields: { fieldName, type, placeholder, required = false } }, index) => {
        return (
          <Input
            key={index}
            label={placeholder + (required ? '*' : '')}
            type={type}
            name={fieldName}
            multiline={fieldName === 'message'}
            value={formData[fieldName]}
            required={required}
            // @ts-ignore FIXME
            validations={type && validations[type]}
          />
        );
      },
    );
  }, [formData, formItems]);

  if (!url) {
    setMessage(
      'Contact form currently unavailable, please try again later or reach out to us at hello@milkywire.com.' as string,
    );
  }

  const changeForm = (event: React.ChangeEvent<HTMLFormElement>) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  async function hubspotSubmit() {
    const context: Record<string, unknown> = { pageName: settings?.identifier ?? '' };
    const submitMessage = await hubSpotSubmit({
      url: url,
      body: prepareFormBody({ type, formData, ...context }),
      successMessage: successMessage,
      errorMessage: errorMessage,
    });
    setFormData(getInitialForm(formItems));
    setMessage(submitMessage);
  }

  const onFormSubmit = async (event: React.ChangeEvent<HTMLFormElement>) => {
    event.stopPropagation();
    event.preventDefault();

    if (type === 'hubspot') {
      hubspotSubmit();
    }
  };

  useEffect(() => {
    const isFormEmpty = checkFormEmpty(formItems, formData);
    if (isFormEmpty !== formEmpty) {
      setFormEmpty(isFormEmpty);
    }
  }, [formData, formEmpty, setFormEmpty, formItems]);

  return (
    <Section disableMaxWidth>
      <RowBlock>
        <SectionBlock tablet="span 6">{contentBlock}</SectionBlock>
        <FormBlock tablet="span 6">
          {message && (
            <MessageWrapper useBuiltInMargins variant="preamble">
              {message}
            </MessageWrapper>
          )}
          {!message && type === 'hubspot' && (
            <Form onSubmit={onFormSubmit} onChange={changeForm}>
              {contentForm}
              <ButtonPromptWrapper prompt={prompt}>
                <ButtonWrapper
                  disabled={formEmpty}
                  color="onLight"
                  label={submitText}
                  tag="button"
                  size="large"
                />
              </ButtonPromptWrapper>
            </Form>
          )}
        </FormBlock>
      </RowBlock>
    </Section>
  );
};

export default FormLayout;
